import {
  BufferGeometry,
  BufferAttribute,
  Vector3,
  Mesh,
  Scene,
  Object3D,
  Texture,
  TextureLoader,
  PlaneGeometry,
  MeshBasicMaterial,
  DoubleSide,
  MathUtils
} from "three";
import {Viewer} from '../main';
import {MarkerMaterial} from "../rendering";
import { CameraImage, PlanarImage } from "../cameras";
import arrowPathStandard from '../../textures/misc/arrow-standard.png';
import arrowPathShadow from '../../textures/misc/arrow-shadow.png';
import { CustomMouseEvent } from "../controls";
import { MarkerNavType } from "../../../redux/settings-slice";
import { RayCaster } from "../ray-caster";

export class CameraMarkers {
  private viewer: Viewer;
  private arrowNavigation: ArrowNavigation;
  public markerNavigation: MarkerNavigation;
  private navigationType: MarkerNavType;
  public cameras: CameraImage[] = [];
  public cameraHeight = 0;
  public modifier = false;
  public scene: Scene = null;

  constructor(viewer) {
    this.viewer = viewer;
    this.initScene();
    this.initKeyDown();

    this.markerNavigation = new MarkerNavigation(viewer, this);
    this.arrowNavigation = new ArrowNavigation(viewer, this);
  }

  get controls() {
    return this.viewer.controls;
  }

  get cameraList() {
    return this.viewer.cameraList;
  }

  get renderInFront() {
    return this.viewer.orbitState || this.arrowNavigation.visible;
  }

  get calculating() {
    return this.markerNavigation.calculating;
  }

  get isHidden() {
    return this.navigationType === MarkerNavType.None;
  }

  get isArrows() {
    return this.navigationType === MarkerNavType.Arrows;
  }

  get isMarkers() {
    return this.navigationType === MarkerNavType.Markers;
  }

  initScene() {
    this.scene = new Scene();
  }

  initKeyDown() {
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    window.addEventListener( 'keydown', this.onKeyDown, false);
    window.addEventListener( 'keyup', this.onKeyUp, false);
  }

  updateCameraMarkers(cameras: Array<CameraImage>) {
    this.cameras = cameras;
    this.markerNavigation.drawCameraMarkers(this.cameras);
  }

  refreshCameraMarkers() {
    const cameras = [...this.cameras];
    this.updateCameraMarkers(cameras);
  }

  setNavigationType(state) {
    this.navigationType = state;
    this.markerNavigation.updatePicker();
  }

  setCameraHeight(height: number) {
    this.cameraHeight = height;
  }

  updateArrowNavigation() {
    this.arrowNavigation.updateNavigation();
  }

  clearArrowNavigation() {
    this.arrowNavigation.clearNavigation();
  }

  onMouseUp(event: CustomMouseEvent): boolean {
    if (event.mouseMoved) {
      return false;
    }

    let camera: CameraImage;

    if (event.isLeftClick) {
      camera = this.markerNavigation.mouseClick(event);
      if (camera) {
        this.viewer.loadImage(camera.id);
        return true;
      }

      camera = this.arrowNavigation.mouseClick(event);
      if (camera) {
        this.viewer.loadImage(camera.id);
        return true;
      }
    }

    return false;
  }

  onMouseMove(event: CustomMouseEvent) {
    if (event.mouseMoved) {
      return;
    }

    // Mouse move for arrow navigation
    this.arrowNavigation.onMouseMove(event);

    // Mouse move for standard navigation
    this.markerNavigation.onMouseMove(event);
  }


  onKeyUp(event) {
    if (event.which !== 17) return;
    this.modifier = false;
  }

  onKeyDown(event) {
    if (event.which !== 17) return;
    this.modifier = true;
  }

  update() {
    let isOrbitMode = this.viewer.orbitState;

    if (this.isHidden) {
      this.markerNavigation.setVisible(false);
      this.arrowNavigation.setVisible(false);
    } else {
      if (this.isMarkers) {
        this.markerNavigation.setVisible(true);
        this.arrowNavigation.setVisible(false);
      } else if (this.isArrows) {
        if (isOrbitMode) {
          this.markerNavigation.setVisible(true);
          this.arrowNavigation.setVisible(false);
        } else {
          this.markerNavigation.setVisible(this.modifier);
          this.arrowNavigation.setVisible(!this.modifier);
        }
      }
    }

    this.markerNavigation.setEnabled(this.isArrows || this.isMarkers);
    this.arrowNavigation.setEnabled(this.isArrows);

    this.markerNavigation.update();
    this.arrowNavigation.update();
  }
}

class MarkerNavigation {
  private viewer: Viewer;
  private markers: CameraMarkers;
  private markerMesh = null;
  private pickingMesh = null;
  private standardMaterial: MarkerMaterial = null;
  private pickingMaterial: MarkerMaterial = null;
  public enabled = false;
  public visible = false;
  private geometry;
  private verticesPerCamera = 6;
  private uvs: number[] = [0, 1, 0, 0, 1, 1, 0, 0, 1, 0, 1, 1];
  private corners: number[] = [0, 2, 1, 2, 3, 1];
  private selectedID = null;
  public markerPositions = [];
  private vertices: Vector3[] = [
    new Vector3(-0.5, 0.5, 0),
    new Vector3(0.5, 0.5, 0),
    new Vector3(-0.5, -0.5, 0),
    new Vector3(0.5, -0.5, 0)
  ]

  private maxIterationTime = 5;
  private calculatedQueue: CameraImage[] = [];
  private calculatedValues = [];
  private calculatedIndex = 0;
  public calculating = false;

  constructor(viewer: Viewer, markers: CameraMarkers) {
    this.markers = markers;
    this.viewer = viewer;

    this.initGeometry();
  }

  get scene() {
    return this.markers.scene;
  }

  get meshes() {
    return [this.markerMesh, this.pickingMesh];
  }

  get materials() {
    return [this.standardMaterial, this.pickingMaterial];
  }

  get picker() {
    return this.viewer.gpuPickers.markers;
  }

  initGeometry() {
    if (this.markerMesh) {
      this.scene.remove(this.markerMesh);
    }

    if (this.pickingMesh) {
      this.picker.remove(this.pickingMesh);
    }

    let geometry = new BufferGeometry();
    geometry.setAttribute('selected', new BufferAttribute(new Float32Array(0), 1));
    geometry.setAttribute('visible', new BufferAttribute(new Float32Array(0), 1));
    geometry.setAttribute('gpu_index', new BufferAttribute(new Int32Array(0), 1));
    geometry.setAttribute('camera_index', new BufferAttribute(new Int32Array(0), 1));
    geometry.setAttribute('position', new BufferAttribute(new Float32Array(0), 3));
    geometry.setAttribute('uv', new BufferAttribute(new Float32Array(0), 2));

    this.standardMaterial = new MarkerMaterial(false);
    this.markerMesh = new Mesh(geometry, this.standardMaterial);
    this.scene.add(this.markerMesh);

    this.pickingMaterial = new MarkerMaterial(true);
    this.pickingMesh = new Mesh(geometry, this.pickingMaterial);
    this.picker.add(this.pickingMesh);
  }

  setEnabled(state) {
    this.enabled = state;
  }

  setVisible(state) {
    if (!this.visible && state) {
      this.updatePicker();
    }

    this.visible = state;
  }

  updatePicker() {
    this.picker.needsUpdate = true;
  }

  drawCameraMarkers(cameras) {
    let numCameras = cameras.length;
    let selected = new Float32Array(numCameras * this.verticesPerCamera);
    let visible = new Float32Array(numCameras * this.verticesPerCamera);
    let markerShape = new Float32Array(numCameras * this.verticesPerCamera);
    let positions = new Float32Array(numCameras * this.verticesPerCamera * 3);
    let uvs = new Float32Array(numCameras * this.verticesPerCamera * 2);
    let indices = new Int32Array(numCameras * this.verticesPerCamera);
    let cameraIndices = new Int32Array(numCameras * this.verticesPerCamera);

    cameras.forEach((camera, cameraIndex) => {
      // Set the marker shape. Default is circular
      let cameraShape = camera instanceof PlanarImage ? 1.0 : 0.0;

      // Assign our uv values
      const numUvs = this.uvs.length;
      this.uvs.forEach((uv, uvIndex) => {
        let index = (numUvs * cameraIndex) + uvIndex;
        uvs[index] = uv;
      });

      // Create attribute arrays for each vertex in each cam.
      const numVertices = this.verticesPerCamera;
      for (var i = 0; i < this.verticesPerCamera; i++) {
        let index = (numVertices * cameraIndex) + i;
        selected[index] = 0.0;
        visible[index] = 1.0;
        indices[index] = 0.0;
        markerShape[index] = cameraShape;
        cameraIndices[index] = cameraIndex;
      }
    });

    let geometry = new BufferGeometry();
    geometry.setAttribute('selected', new BufferAttribute(selected, 1));
    geometry.setAttribute('visible', new BufferAttribute(visible, 1));
    geometry.setAttribute('marker_shape', new BufferAttribute(markerShape, 1));
    geometry.setAttribute('gpu_index', new BufferAttribute(indices, 1));
    geometry.setAttribute('camera_index', new BufferAttribute(cameraIndices, 1));
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
    this.geometry = geometry;

    this.setDataToCalculate(cameras);

    if (numCameras === 0) {
      this.updateGeometry();
    }
  }

  markCameraSelected(markerID) {
    this.meshes.forEach(mesh => {
      let attributes = mesh.geometry.attributes;
      attributes.selected.array.fill(0.0);

      let index = attributes.camera_index.array.indexOf(markerID);
      if (index !== -1) {
        for (var i = 0; i < this.verticesPerCamera; i++) {
          attributes.selected.array[index + i] = 1.00;
        }
      }

      attributes.selected.needsUpdate = true;
    });

    if (this.selectedID !== markerID) {
      // Update picker if selected marker changed
      this.updatePicker();
    }

    this.selectedID = markerID;
  }

  setDataToCalculate(cameras: CameraImage[]) {
    this.calculatedIndex = 0;
    this.calculatedQueue = cameras;
    this.calculatedValues = new Array(cameras.length);
    this.calculating = cameras.length > 0;
  }

  calculateCameras() {
    let numCameras = this.calculatedQueue.length;
    if (numCameras === 0) return;

    let timeStart = performance.now();
    while (this.calculatedIndex < numCameras) {
      let camera = this.calculatedQueue[this.calculatedIndex];
      let position = camera.position.viewer;
      this.calculatedValues[this.calculatedIndex] = position;
      this.calculatedIndex += 1;

      let elapsed = performance.now() - timeStart;
      if (elapsed > this.maxIterationTime) break;
    }

    // Exit early and wait until next update
    if (this.calculatedIndex < numCameras) return;

    let positions = new Float32Array(numCameras * this.verticesPerCamera * 3);

    this.markerPositions = new Array(numCameras);
    this.calculatedValues.forEach((position, cameraIndex) => {
      // Setting our positions array
      const numCorners = this.corners.length;
      this.markerPositions[cameraIndex] = position;
      this.corners.forEach((corner, cornerIndex) => {
        let index = (numCorners * cameraIndex) + cornerIndex;
        var points = this.vertices[corner];
        positions[(index * 3)] = points.x + position.x;
        positions[(index * 3) + 1] = points.y + position.y;
        positions[(index * 3) + 2] = points.z + position.z;
      });
    });

    this.geometry.setAttribute('position', new BufferAttribute(positions, 3));
    this.geometry.attributes.position.needsUpdate = true;

    this.setDataToCalculate([]);
    this.updateGeometry();
  }

  updateGeometry() {
    this.meshes.forEach(mesh => {
      mesh.geometry.dispose();
      mesh.geometry.copy(this.geometry);
      mesh.geometry.needsUpdate = true;
    });

    this.viewer.updatePickers();
  }

  cameraPickerID(event) {
    try {
      // Update gpu picker if required
      this.picker.update();

      // Grab our index and marker id
      const index = this.picker.value(event);

      if (index === null) {
        return null;
      }

      const attributes = this.pickingMesh.geometry.attributes;
      const arrIndex = attributes.gpu_index.array.indexOf(index);
      const markerID = attributes.camera_index.array[arrIndex];

      return markerID as number;
    }
    catch (e) {
      console.log("Prevented framebuffer exception: ");
      console.log(e);

      return null;
    }
  }

  onMouseMove(event)  {
    let markerID = this.cameraPickerID(event);
    this.markCameraSelected(markerID);
  }

  mouseClick(event) : CameraImage {
    let markerID = this.cameraPickerID(event);

    return (markerID !== null)
      ? this.markers.cameras[markerID]
      : null;
  }

  update() {
    const camera = this.viewer.camera;
    const position = camera.position;
    const rotation = camera.rotation.toVector3();
    const cameraIndex = this.viewer.getCurrentCameraIndex();
    const initialCameraLoading = this.viewer.initialCameraLoading;

    // Set meshes visibility
    this.meshes.forEach(mesh => {
      mesh.visible = this.visible;
    });

    // Update shader parameters
    this.materials.forEach((material: MarkerMaterial) => {
      material.currentCameraRotation = rotation;
      material.currentCameraPosition = position;
      material.windowHeight = window.innerHeight;
      material.initialCameraLoading = initialCameraLoading;
      material.currentIndex = cameraIndex;
      material.orbitMode = this.viewer.orbitState;
      material.cameraHeight = this.markers.cameraHeight;
      material.fov = MathUtils.degToRad(camera.fov);
      material.needsUpdate = true;
    });

    this.calculateCameras();
  }
}

class ArrowMarker {
  public camera: CameraImage;
  private radius = 1.5
  private height = -1.0;
  private size = 0.5;
  public object: Object3D;

  constructor(textures, section, angle) {
    this.object = new Object3D();
    this.object.rotation.z = 1 * MathUtils.degToRad(angle);
    this.addMesh(section, textures[0], this.height);
    this.addMesh(section, textures[1], this.height * 1.03);
  }

  get visible() {
    return this.object.visible;
  }

  get enabled() {
    return this.camera !== null;
  }

  clear() {
    this.camera = null;
    this.setVisible(false);
  }

  setVisible(state) {
    this.object.visible = state;
  }

  setCamera(camera: CameraImage) {
    this.camera = camera;
  }

  setScale(scale) {
    this.object.children.forEach(x => {
      x.scale.copy(scale);
    });
  }

  addMesh(section, texture, height) {
    // Add standard marker mesh
    const geometryStandard = new PlaneGeometry(this.size * 0.75, this.size);
    const materialStandard = new MeshBasicMaterial({
      map: texture,
      side: DoubleSide,
      transparent: true,
      alphaTest: 0.5
    });

    const mesh = new Mesh(geometryStandard, materialStandard);
    mesh.userData = {section};
    mesh.position.x = this.radius;
    mesh.position.z = height;
    mesh.rotation.z = -1 * MathUtils.degToRad(90);
    this.object.add(mesh);
  }
}

class ArrowNavigation {
  private viewer: Viewer;
  private markers: CameraMarkers;
  private object: Object3D;
  private textures: Texture[] = [];
  public enabled = false;
  public visible = false;
  private minDistance = 5.0;
  private maxDistance = 40.0;
  private numSections = 8;
  private needsUpdate;
  private sections: ArrowMarker[] = [];
  private defaultScale = new Vector3(1.0, 1.0, 1.0);
  private zoomedScale = new Vector3(1.2, 1.2, 1.2);

  constructor(viewer: Viewer, markers: CameraMarkers) {
    this.markers = markers;
    this.viewer = viewer;

    this.loadTextures();
    this.initMeshObject();
    this.clearNavigation();
  }

  get scene() {
    return this.markers.scene;
  }

  initMeshObject() {
    this.object = new Object3D();

    for (let section=0;section<this.numSections;section++) {
      let angle = this.sectionToAngle(section);
      let arrow = new ArrowMarker(this.textures, section, angle);

      this.object.add(arrow.object);
      this.sections.push(arrow);
    }

    this.scene.add(this.object);
  }

  loadTextures() {
    const loader = new TextureLoader();
    this.textures.push(loader.load(arrowPathStandard));
    this.textures.push(loader.load(arrowPathShadow));
  }

  setEnabled(state) {
    this.enabled = state;
  }

  setVisible(state) {
    this.visible = state;
  }

  angleToSection(angle) {
    let angleMod = (angle + 360) % 360;
    let section = angleMod / (360 / this.numSections);
    return Math.round(section % (this.numSections - 0.5));
  }

  sectionToAngle(section) {
    return section * (360 / this.numSections);
  }

  updateNavigation() {
    this.needsUpdate = true;
  }

  clearNavigation() {
    this.sections.forEach(arrow => {
      arrow.clear();
    });
  }

  calculateCameras() {
    if (this.markers.calculating || !this.visible || !this.needsUpdate) {
      return;
    }

    this.needsUpdate = false;

    // Positions are precalculated when adding scene view markers
    let positions = this.markers.markerNavigation.markerPositions;

    this.clearNavigation();

    const cameraPos = this.viewer.camera.position;

    const validCameras = [];
    positions.forEach((markerPos, cameraIndex) => {
      let x = markerPos.x - cameraPos.x;
      let y = markerPos.y - cameraPos.y;

      const distance = Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2));

      // Calculate min and max distance from camera;
      let tooClose = distance < this.minDistance;
      let tooFar = distance > this.maxDistance;
      if (tooClose || tooFar) {
        return;
      }

      // Calculate section
      let angle = Math.atan2(y, x) * 180 / Math.PI;
      let section = this.angleToSection(angle);

      let camera = this.markers.cameras[cameraIndex];
      validCameras.push({camera, distance, section});
    });

    // Sort by distance
    validCameras.sort((a, b) => {
      return a.distance - b.distance;
    });

    validCameras.forEach(({camera, section}) => {
      if (this.sections[section].enabled) {
        return;
      }

      this.sections[section].setCamera(camera);
      this.sections[section].setVisible(true);
    });

    // Update mesh position
    this.object.position.copy(cameraPos);
  }

  onMouseMove(event) {
    this.sections.forEach(arrow => {
      arrow.setScale(this.defaultScale);
    });

    let object = this.getIntersect(event);
    if (!object) {
      return;
    }

    object.setScale(this.zoomedScale);
  }

  mouseClick(event) {
    let object = this.getIntersect(event);
    if (!object || !object.camera) {
      return null;
    }

    this.sections.forEach(arrow => {
      arrow.setVisible(false);
    });

    return object.camera;
  }

  /** @private */
  getIntersect(event) : ArrowMarker {
    if (!this.visible) {
      return;
    }

    let targets = this.sections.map(arrow => {
      return arrow.object.children[0];
    });

    let raycaster = new RayCaster(this.viewer, event);
    let intersects = raycaster.intersectObjects(targets);
    if (intersects.length === 0) {
      return;
    }

    const intersect = intersects[0].object;
    const sectionID = intersect.userData.section;
    const object = this.sections[sectionID];

    return object;
  }

  update() {
    if (!this.object) {
      return;
    }

    this.calculateCameras();
    this.object.visible = this.visible;
  }
}
